import { Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField } from "@mui/material";
import React from "react";
import downloadjs from "downloadjs";
import * as calendarTaskService from "source/models/services/calendarTaskService";
import * as attachmentService from "source/models/services/attachmentService";
import { ISessionState } from "source/models/session/sessionState";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { AttachmentList } from "../../../attachments/AttachmentList";
import { DatePicker, TimePicker } from "../../../framework/DateTime";
import { DialogSectionCard } from "../../../framework/Dialog/DialogSectionCard";
import { FormField } from "../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../framework/Grid/GridRow";
import { CalendarTaskState, ConfirmationDialogResult, DatabaseRecordType, UserGroupType } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { ICalendarTaskCategoryListItem } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryListItem";
import { TaskStateListChip } from "source/components/task/TaskStateListChip";
import { UserGroupBlocks } from "source/components/userGroup/UserGroupBlocks";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { CalendarTaskReminderOptions } from "../../CalendarTaskPage/CalendarTaskPageUtils";

interface CalendarTaskBasicInformationProps {
    sessionState: ISessionState;
    categories: ICalendarTaskCategoryListItem[];
    isReadOnly: boolean;
    canShareCalendarTasks: boolean;
    companyId: string;
    isNew: boolean;
    lockedUser: IModifyUser;

    id: string;
    categoryId: string;
    recordId: string;
    databaseRecordType: DatabaseRecordType;
    title: string;
    locationText: string;
    state: CalendarTaskState;
    description: string;
    startDateStr: string;
    startTimeStr: string;
    endDateStr: string;
    endTimeStr: string;
    allDay: boolean;
    isPrivate: boolean;
    remainder: string;
    attachments: IFileAttachment[];
    handlers: IModifyUser[];

    onChange: (name: string, value: string | number | boolean) => void;
    onChangeHandlers: (handlers: IModifyUser[]) => void;
    onChangeAttachments: (attachments: IFileAttachment[]) => void;
    onUnlockAndLockToMe: () => void;
}

export const CalendarTaskBasicInformation = (
    props: CalendarTaskBasicInformationProps
) => {
    const handleChange = (event) => {
        const target = event.target;
        props.onChange(target.name, target.value);
    };

    // #region Handlers
    const addHandlers = async (userGroups: IModifyUser[], showSuccessMessage: boolean): Promise<ApiSuccess> => {
        if (!userGroups || userGroups.length < 1) return null;
        if (!props.isNew) {
            const result = await AppUtils.callService(() => calendarTaskService.calendarTaskEditAddHandlers(props.companyId, props.id, userGroups.map(i => ({ code: i.code, type: i.date }))), showSuccessMessage);
            if (!result) return null;
        }
        props.onChangeHandlers(ModifyUser.sortModifyUsers(props.handlers.concat(userGroups)));
        return new ApiSuccess();
    };

    const handleAddHandlers = async (users: IModifyUser[]): Promise<ApiSuccess> => {
        return addHandlers(users, true);
    };

    const handleRemoveHandlers = async (userGroupCodes: string[]): Promise<ApiSuccess> => {
        if (!userGroupCodes || userGroupCodes.length < 1) return null;
        if (!props.isNew) {
            const removeHandlers = props.handlers.filter(i => userGroupCodes.indexOf(i.code) >= 0);
            const result = await AppUtils.callService(() => calendarTaskService.calendarTaskEditRemoveHandlers(props.companyId, props.id, removeHandlers.map(i => ({ code: i.code, type: i.date }))));
            if (!result) return null;
        }
        props.onChangeHandlers(ModifyUser.sortModifyUsers(props.handlers.filter(i => userGroupCodes.indexOf(i.code) < 0)));
        return new ApiSuccess();
    };
    // #endregion Handlers

    // #region Attachments
    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = props.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    const getRemoveAttachmentsSaveData = (attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.id);
        result.append("companyId", props.companyId);
        result.append("ids", attachmentIds);
        return result;
    };

    const handleAttachmentRemove = async (id: string) => {
        if (!id) return;
        if (!props.isNew) {
            const saveData = getRemoveAttachmentsSaveData([id]);
            if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, props.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
            const result = AppUtils.callService(() => calendarTaskService.calendarTaskEditRemoveAttachments(saveData.obj));
            if (!result) return;
        }
        props.onChangeAttachments(props.attachments.filter(i => i.id !== id));
    };

    const getAddAttachmentsSaveData = async (attachments: IFileAttachment[]): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.id);
        result.append("companyId", props.companyId);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments));
        return result;
    };
    
    const handleAttachmentsAdd = async (fileList: FileList) => {
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, 0);
        if (attachments.length < 1) return;
        if (!props.isNew) {
            const saveData = await getAddAttachmentsSaveData(attachments);
            const result = await AppUtils.callService(() => calendarTaskService.calendarTaskEditAddAttachments(saveData.obj));
            if (!result) return;
            EditItem.setItemIdsFromIds(attachments, result.ids);
        }
        props.onChangeAttachments(props.attachments.concat(attachments));
    };
    // #endregion Attachments
    
    const isRelatedToRecord = !Base.isNullOrUndefined(props.databaseRecordType);

    return (
        <GridRow>
            <Grid item sm={8}>
                <DialogSectionCard headerTitle={Translations.BasicInformation}>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.Title} required margin={0}>
                                <TextField
                                    name="title"
                                    value={props.title}
                                    disabled={props.isReadOnly}
                                    required={true}
                                    title={Translations.Title}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleChange}
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.Location} margin={0}>
                                <TextField
                                    name="locationText"
                                    value={props.locationText}
                                    disabled={isRelatedToRecord || props.isReadOnly}
                                    title={!isRelatedToRecord ? Translations.Location : props.locationText}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleChange}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Starts} required>
                                <GridRow>
                                    <Grid item sm={3}>
                                        <DatePicker
                                            disabled={props.isReadOnly}
                                            value={props.startDateStr}
                                            onChange={(value: string) => props.onChange("startDateStr", value)}
                                        />
                                    </Grid>
                                    <Grid item sm={3}>
                                        <TimePicker
                                            disabled={props.allDay || props.isReadOnly}
                                            value={props.startTimeStr}
                                            onChange={(value: string) => props.onChange("startTimeStr", value)}
                                        />
                                    </Grid>
                                    <Grid item sm={3}>
                                        <FormControlLabel
                                            label={Translations.AllDay}
                                            control={
                                                <Checkbox
                                                    disabled={props.isReadOnly}
                                                    checked={props.allDay}
                                                    onChange={(_e, checked: boolean) => props.onChange("allDay", checked)}
                                                />
                                            }
                                        />
                                    </Grid>
                                    {!isRelatedToRecord &&
                                        <Grid item sm={3}>
                                            <FormControlLabel
                                                label={Translations.Private}
                                                control={
                                                    <Checkbox
                                                        checked={props.isPrivate}
                                                        disabled={props.isReadOnly || !props.canShareCalendarTasks}
                                                        onChange={(_e, checked: boolean) => props.onChange("isPrivate", checked)}
                                                    />
                                                }
                                            />
                                        </Grid>
                                    }
                                </GridRow>
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.Ends}>
                                <GridRow>
                                    <Grid item sm={6}>
                                        <DatePicker
                                            disabled={props.isReadOnly}
                                            value={props.endDateStr}
                                            minDate={props.startDateStr}
                                            onChange={(value: string) => props.onChange("endDateStr", value)}
                                        />
                                    </Grid>
                                    <Grid item sm={6}>
                                        <TimePicker
                                            disabled={props.allDay || props.isReadOnly}
                                            value={props.endTimeStr}
                                            minTime={props.startDateStr === props.endDateStr ? props.startTimeStr : undefined}
                                            onChange={(value: string) => props.onChange("endTimeStr", value)}
                                        />
                                    </Grid>
                                </GridRow>
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.Reminder}>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <Select value={props.remainder} disabled={props.isReadOnly} onChange={(e) => props.onChange("remainder", e.target.value)}>
                                            {CalendarTaskReminderOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                </GridRow>
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Description}>
                                <TextField
                                    name="description"
                                    value={props.description}
                                    multiline
                                    inputProps={{ maxLength: 1000 }}
                                    minRows={4}
                                    disabled={props.isReadOnly}
                                    onChange={handleChange}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12} md={6}>
                            <FormField title={Translations.State}>
                                <Select value={props.state} disabled={props.isReadOnly} onChange={(e) => (props.onChange("state", e.target.value))}>
                                    <MenuItem value={CalendarTaskState.NotStarted}>{Translations.CalendarTaskStateNotStarted}</MenuItem>
                                    <MenuItem value={CalendarTaskState.Started}>{Translations.CalendarTaskStateStarted}</MenuItem>
                                    <MenuItem value={CalendarTaskState.Finished}>{Translations.CalendarTaskStateFinished}</MenuItem>
                                </Select>
                            </FormField>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormField title={Translations.CalendarTaskCategory}>
                                <Select value={props.categoryId} disabled={props.isReadOnly} onChange={(e) => (props.onChange("categoryId", e.target.value))}>
                                    {!isRelatedToRecord &&
                                        <MenuItem value="">{`'${Translations.SelectCategory}'`}</MenuItem>
                                    }
                                    {props.categories.map((category) =>
                                        <MenuItem key={category.id} value={category.id}>
                                            <TaskStateListChip
                                                code={category.code}
                                                color={category.color}
                                            />
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormField>
                        </Grid>
                    </GridRow>
                </DialogSectionCard>
            </Grid>
            <Grid item sm={4}>
                <GridRow height={props.isPrivate || isRelatedToRecord ? "calc(100% + 16px)" : "calc(50% + 16px)"}>
                    <Grid item sm={12} height="100%">
                        <DialogSectionCard headerTitle={Translations.Attachments} height="100%">
                            <AttachmentList
                                isReadOnly={props.isReadOnly}
                                title=""
                                margin={0}
                                attachments={props.attachments}
                                onRemoveAttachment={handleAttachmentRemove}
                                onDownloadAttachment={handleAttachmentDownload}
                                onAddAttachments={handleAttachmentsAdd}
                            />
                        </DialogSectionCard>
                    </Grid>
                </GridRow>
                {!props.isPrivate && !isRelatedToRecord &&
                    <GridRow height="50%">
                        <Grid item sm={12} height="100%">
                            <DialogSectionCard headerTitle={Translations.CalendarTaskHandlers} height="100%" margin="1rem 0 0 0" flexGrow={2}>
                                <UserGroupBlocks
                                    isReadOnly={props.isReadOnly}
                                    companyId={props.companyId}
                                    defaultUserGroupType={UserGroupType.Fixed}
                                    listModeChangeDisabled={false}
                                    margin={0}
                                    title={null}
                                    selectionTitle={Translations.SelectCalendarTaskHandlers}
                                    noUsersMessage={Translations.AllCalendarTaskUsers}
                                    userGroups={props.handlers}
                                    removeUsersConfirmation={Translations.DoYouReallyWantToRemoveSelectedCalendarTaskHandlers}
                                    onAddUserGroups={handleAddHandlers}
                                    onRemoveUserGroups={handleRemoveHandlers}
                                    onGetUserGroupSelectListItems={calendarTaskService.getUserGroupListItems}
                                />
                            </DialogSectionCard>
                        </Grid>
                    </GridRow>
                }
            </Grid>
        </GridRow>
    );
};
